import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrustedPersonService, useTrustedPersons } from '@lemontaigu/staff/react';
import {
  TRUSTED_RETENTION_MAX_DAYS,
  TRUSTED_RETENTION_MIN_DAYS,
  businessDayFetchRange,
  entryHours,
  presenceReport,
  presentNow,
  trustedPersonLabel,
  type TrustedEntry,
  type TrustedPersonRecord,
} from '@lemontaigu/staff';
import {
  dayKey,
  dayRange,
  fmtDayLabel,
  fmtHours,
  fmtTime,
  fromDatetimeLocal,
  shiftDay,
  summaryRange,
  toDatetimeLocal,
  type SummaryGranularity,
} from './logic.js';
import { PeriodNav } from './Pointages.js';

// « Hors Staff » (RH group — ADR-0024, docs/NON-EMPLOYEE-PRESENCE-BRIEF.md).
//
// Recurring trusted people (volunteers, family, seasonal helpers) who clock in on
// the reception kiosk WITHOUT a contract, so the operator knows who is on the
// premises off-season. This page is presence only: who is in now, a day-by-day
// in/out journal, and the people themselves. Hours of PRESENCE appear on the
// printable report only (operator, 2026-10-02); there is deliberately no contract,
// no overtime and no comparison with a plan — nothing that reads as payroll or
// as employment.

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function HorsStaff() {
  const store = useTrustedPersons();
  const service = useTrustedPersonService();

  // « Journal » is the live page; « Détail par personne » is the printable report.
  const [mode, setMode] = useState<'presences' | 'detail'>('presences');
  const [people, setPeople] = useState<TrustedPersonRecord[] | null>(null);
  const [retention, setRetention] = useState<number | null>(null);
  const [retentionDraft, setRetentionDraft] = useState('');
  const [day, setDay] = useState(() => dayKey(new Date()));
  const [dayEntries, setDayEntries] = useState<TrustedEntry[]>([]);
  const [recent, setRecent] = useState<TrustedEntry[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const nameOf = useMemo(() => {
    const m = new Map((people ?? []).map((p) => [p.id, trustedPersonLabel(p)]));
    return (id: string) => m.get(id) ?? 'Personne supprimée';
  }, [people]);

  const load = useCallback(async () => {
    setErr('');
    try {
      const now = new Date();
      const today = dayKey(now);
      const [list, days, entriesForDay, recentEntries] = await Promise.all([
        store.list(),
        store.retentionDays(),
        store.entriesBetween(dayRange(day).fromIso, dayRange(day).toIso),
        // Yesterday → tomorrow: enough to see who is in now, and to catch a forgotten clock-out.
        store.entriesBetween(dayRange(shiftDay(today, -1)).fromIso, dayRange(shiftDay(today, 1)).toIso),
      ]);
      setPeople(list);
      setRetention(days);
      setRetentionDraft((d) => (d === '' ? String(days) : d));
      setDayEntries(entriesForDay);
      setRecent(recentEntries);
    } catch (e) {
      setErr(errMsg(e));
    }
  }, [store, day]);

  useEffect(() => {
    void load();
  }, [load]);

  // Anything that writes: one place for busy / error / reload.
  const act = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setErr(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const { present, forgottenOut } = presentNow(recent, Date.now());
  const active = (people ?? []).filter((p) => p.archivedAt === null);

  return (
    <div className="equipe hs-page">
      <div className="equipe-head no-print">
        <h2 className="section-title">Hors Staff — présences</h2>
        <span className="equipe-hint">
          Personnes de confiance sans contrat (bénévoles, famille, saisonniers) qui pointent sur la
          tablette de la réception. Présence uniquement : pas de contrat ni de paie ; les heures de présence figurent sur le relevé imprimable (onglet « Détail par personne »).
        </span>
      </div>

      <div className="pointages-modes no-print">
        <button type="button" className={mode === 'presences' ? 'is-active' : ''} onClick={() => setMode('presences')}>
          Présences
        </button>
        <button type="button" className={mode === 'detail' ? 'is-active' : ''} onClick={() => setMode('detail')}>
          Détail par personne
        </button>
      </div>

      {err && <p className="auth-err no-print">Erreur : {err}</p>}

      {mode === 'detail' && <PersonReportView people={people ?? []} />}

      {mode === 'presences' && (
        <>
      <section className="hs-section">
        <h3>Présent·es maintenant ({present.length})</h3>
        {people === null && <p className="staff-empty">Chargement…</p>}
        {people !== null && present.length === 0 && <p className="staff-empty">Personne en ce moment.</p>}
        <ul className="hs-list">
          {present.map((e) => (
            <li key={e.id}>
              <strong>{nameOf(e.personId)}</strong> — arrivé·e à {fmtTime(e.clockIn)}
            </li>
          ))}
        </ul>
        {forgottenOut.length > 0 && (
          <p className="ts-warning">
            Départ non pointé :{' '}
            {forgottenOut.map((e) => `${nameOf(e.personId)} (arrivé·e le ${fmtDayLabel(dayKey(new Date(e.clockIn)))})`).join(', ')}
            . Corrigez le journal ci-dessous.
          </p>
        )}
      </section>

      <section className="hs-section">
        <div className="hs-day-nav">
          <button type="button" className="auth-btn" onClick={() => setDay(shiftDay(day, -1))}>
            ←
          </button>
          <h3>Journal — {fmtDayLabel(day)}</h3>
          <button type="button" className="auth-btn" onClick={() => setDay(shiftDay(day, 1))}>
            →
          </button>
          <button type="button" className="auth-btn" onClick={() => setDay(dayKey(new Date()))}>
            Aujourd’hui
          </button>
        </div>
        {dayEntries.length === 0 ? (
          <p className="staff-empty">Aucun pointage ce jour-là.</p>
        ) : (
          <table className="ts-table">
            <thead>
              <tr>
                <th>Personne</th>
                <th>Arrivée</th>
                <th>Départ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dayEntries.map((e) => (
                <EntryRow
                  key={e.id}
                  entry={e}
                  name={nameOf(e.personId)}
                  busy={busy}
                  onSave={(inIso, outIso) => act(() => service.saveEntry(e.id, e.personId, inIso, outIso))}
                  onDelete={() => act(() => service.removeEntry(e.id))}
                />
              ))}
            </tbody>
          </table>
        )}
        <AddEntry
          people={active}
          day={day}
          busy={busy}
          onAdd={(personId, inIso, outIso) => act(() => service.saveEntry(null, personId, inIso, outIso))}
        />
      </section>

      <section className="hs-section">
        <h3>Personnes</h3>
        {people !== null && people.length === 0 && <p className="staff-empty">Personne pour l’instant.</p>}
        <table className="ts-table">
          <tbody>
            {(people ?? []).map((p) => (
              <tr key={p.id} className={p.archivedAt ? 'hs-archived' : ''}>
                <td>{trustedPersonLabel(p)}</td>
                <td className={p.hasPin ? 'emp-ok' : 'emp-warn'}>{p.hasPin ? '● PIN' : '○ PIN à définir'}</td>
                <td className="hs-actions">
                  <button
                    type="button"
                    className="auth-btn"
                    disabled={busy || p.archivedAt !== null}
                    onClick={() => {
                      const pin = window.prompt(`Nouveau code PIN (4 à 8 chiffres) pour ${trustedPersonLabel(p)} :`);
                      if (pin !== null) void act(() => service.setPin(p.id, pin.trim()));
                    }}
                  >
                    {p.hasPin ? 'Changer le PIN' : 'Définir le PIN'}
                  </button>
                  <button
                    type="button"
                    className="auth-btn"
                    disabled={busy}
                    onClick={() => void act(() => service.setArchived(p.id, p.archivedAt === null))}
                  >
                    {p.archivedAt === null ? 'Archiver' : 'Réactiver'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="equipe-hint">
          Une personne archivée n’apparaît plus sur la tablette et ne peut plus pointer ; ses pointages
          restent consultables jusqu’à leur suppression automatique.
        </p>
        <AddPerson
          busy={busy}
          onAdd={(first, last, nick, pin) =>
            act(async () => {
              const id = await service.addPerson(first, last, nick);
              if (pin) await service.setPin(id, pin);
            })
          }
        />
      </section>

      <section className="hs-section">
        <h3>Conservation</h3>
        <p className="equipe-hint">
          Les pointages Hors Staff sont supprimés automatiquement au bout de{' '}
          <strong>{retention ?? '…'} jours</strong> (par défaut 120 ; maximum {TRUSTED_RETENTION_MAX_DAYS}). Les
          pointages des salarié·es ne sont jamais concernés.
        </p>
        <form
          className="hs-form"
          onSubmit={(ev) => {
            ev.preventDefault();
            void act(() => service.setRetentionDays(Number(retentionDraft)));
          }}
        >
          <label>
            Durée (jours)
            <input
              type="number"
              min={TRUSTED_RETENTION_MIN_DAYS}
              max={TRUSTED_RETENTION_MAX_DAYS}
              value={retentionDraft}
              onChange={(e) => setRetentionDraft(e.target.value)}
            />
          </label>
          <button type="submit" className="auth-btn" disabled={busy}>
            Enregistrer
          </button>
        </form>
      </section>
        </>
      )}
    </div>
  );
}

/**
 * « Détail par personne » — the printable relevé: one person, one period, every
 * day they came with arrival → departure. Same period navigator and print path
 * as Pointages → Détail par personne. Shows hours of PRESENCE (per visit, per day,
 * per period) but no « repos » rows (a volunteer is not owed days) and no contract
 * comparison or overtime — ADR-0024.
 */
function PersonReportView({ people }: { people: TrustedPersonRecord[] }) {
  const store = useTrustedPersons();
  const [personId, setPersonId] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [granularity, setGranularity] = useState<SummaryGranularity>('month');
  const [anchor, setAnchor] = useState(() => dayKey(new Date()));
  const [entries, setEntries] = useState<TrustedEntry[] | null>(null);
  const [err, setErr] = useState('');
  const { fromDay, toDay } = summaryRange(granularity, anchor);

  useEffect(() => {
    if (personId === '') {
      setEntries(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      setErr('');
      try {
        // Superset window; presenceReport does the exact business-day filtering.
        const w = businessDayFetchRange(fromDay, toDay);
        const rows = await store.entriesBetween(w.fromIso, w.toIso);
        if (!cancelled) setEntries(rows);
      } catch (e) {
        if (!cancelled) setErr(errMsg(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store, personId, fromDay, toDay]);

  const person = people.find((p) => p.id === personId) ?? null;
  const report = useMemo(
    () => (entries === null || personId === '' ? null : presenceReport(entries, personId, fromDay, toDay)),
    [entries, personId, fromDay, toDay],
  );

  return (
    <>
      <div className="ts-controls no-print">
        <label className="ts-field">
          <span>Personne</span>
          <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
            <option value="">— choisir —</option>
            {people
              .filter((p) => showArchived || p.archivedAt === null || p.id === personId)
              .sort((a, b) => a.firstName.localeCompare(b.firstName, 'fr'))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {trustedPersonLabel(p)}
                  {p.archivedAt ? ' (archivé·e)' : ''}
                </option>
              ))}
          </select>
        </label>
        <label className="ts-field ts-field-check">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          <span>Inclure les archivé·e·s</span>
        </label>
        <PeriodNav granularity={granularity} anchor={anchor} onGranularity={setGranularity} onAnchor={setAnchor} />
        {report !== null && (
          <button type="button" className="auth-btn ts-print" onClick={() => window.print()}>
            Imprimer
          </button>
        )}
      </div>

      {err && <p className="auth-err no-print">Erreur : {err}</p>}
      {personId === '' && <p className="staff-empty no-print">Choisissez une personne pour afficher ses présences.</p>}

      {report !== null && person !== null && (
        <section className="ts-report">
          <header className="ts-report-head">
            <h2 className="section-title">Relevé de présence — {trustedPersonLabel(person)}</h2>
            <div className="ts-report-range">
              Du {fmtDayLabel(report.fromDay)} au {fmtDayLabel(report.toDay)}
            </div>
            <div className="ts-report-range">
              Hors Staff · édité le {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </header>

          <div className="ts-summary">
            <div className="ts-summary-item">
              <span className="ts-summary-value">{fmtHours(report.totalHours)}</span>
              <span className="ts-summary-label">de présence</span>
            </div>
            <div className="ts-summary-item">
              <span className="ts-summary-value">{report.daysPresent}</span>
              <span className="ts-summary-label">jour{report.daysPresent > 1 ? 's' : ''} de présence</span>
            </div>
            <div className="ts-summary-item">
              <span className="ts-summary-value">{report.visits}</span>
              <span className="ts-summary-label">passage{report.visits > 1 ? 's' : ''}</span>
            </div>
          </div>

          {report.openCount > 0 && (
            <p className="ts-warning">
              {report.openCount} pointage{report.openCount > 1 ? 's' : ''} sans départ sur la période — non compté
              dans les heures ; à corriger dans l’onglet Présences.
            </p>
          )}

          {report.days.length === 0 ? (
            <p className="staff-empty">Aucune présence sur la période.</p>
          ) : (
            <table className="ts-table">
              <thead>
                <tr>
                  <th>Jour</th>
                  <th>Arrivée → Départ</th>
                  <th className="ts-num">Heures</th>
                  <th className="ts-num">Total jour</th>
                </tr>
              </thead>
              <tbody>
                {report.days.map((d) =>
                  d.shifts.map((s, i) => (
                    <tr key={s.id} className={s.clockOut === null ? 'ts-open' : ''}>
                      {i === 0 && (
                        <td rowSpan={d.shifts.length} className="ts-day">
                          {fmtDayLabel(d.day)}
                        </td>
                      )}
                      <td>
                        {fmtTime(s.clockIn)} → {s.clockOut ? fmtTime(s.clockOut) : <em>sans départ — à corriger</em>}
                      </td>
                      <td className="ts-num">{entryHours(s) === null ? '—' : fmtHours(entryHours(s) ?? 0)}</td>
                      {i === 0 && (
                        <td rowSpan={d.shifts.length} className="ts-num">
                          {fmtHours(d.totalHours)}
                        </td>
                      )}
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          )}
        </section>
      )}
    </>
  );
}

function EntryRow({
  entry,
  name,
  busy,
  onSave,
  onDelete,
}: {
  entry: TrustedEntry;
  name: string;
  busy: boolean;
  onSave: (clockInIso: string, clockOutIso: string | null) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [inValue, setInValue] = useState('');
  const [outValue, setOutValue] = useState('');
  const [localErr, setLocalErr] = useState('');

  const start = () => {
    setInValue(toDatetimeLocal(entry.clockIn));
    setOutValue(entry.clockOut ? toDatetimeLocal(entry.clockOut) : '');
    setLocalErr('');
    setEditing(true);
  };

  const save = async () => {
    const clockIn = fromDatetimeLocal(inValue);
    const clockOut = fromDatetimeLocal(outValue);
    if (!clockIn) return setLocalErr('Heure d’arrivée invalide.');
    if (clockOut !== null && clockOut <= clockIn) return setLocalErr('Le départ doit suivre l’arrivée.');
    if (await onSave(clockIn, clockOut)) setEditing(false);
  };

  return (
    <tr className={entry.clockOut === null ? 'ts-open' : ''}>
      <td>{name}</td>
      {editing ? (
        <>
          <td>
            <input type="datetime-local" value={inValue} onChange={(e) => setInValue(e.target.value)} />
          </td>
          <td>
            <input type="datetime-local" value={outValue} onChange={(e) => setOutValue(e.target.value)} />
            {localErr && <span className="auth-err"> {localErr}</span>}
          </td>
          <td className="hs-actions">
            <button type="button" className="auth-btn" disabled={busy} onClick={() => void save()}>
              Enregistrer
            </button>
            <button type="button" className="auth-btn" onClick={() => setEditing(false)}>
              Annuler
            </button>
          </td>
        </>
      ) : (
        <>
          <td>{fmtTime(entry.clockIn)}</td>
          <td>{entry.clockOut ? fmtTime(entry.clockOut) : <em>sans départ — à corriger</em>}</td>
          <td className="hs-actions">
            <button type="button" className="auth-btn" disabled={busy} onClick={start}>
              Corriger
            </button>
            <button
              type="button"
              className="auth-btn ts-danger"
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Supprimer ce pointage de ${name} ?`)) void onDelete();
              }}
            >
              Supprimer
            </button>
          </td>
        </>
      )}
    </tr>
  );
}

function AddEntry({
  people,
  day,
  busy,
  onAdd,
}: {
  people: TrustedPersonRecord[];
  day: string;
  busy: boolean;
  onAdd: (personId: string, clockInIso: string, clockOutIso: string | null) => Promise<boolean>;
}) {
  const [personId, setPersonId] = useState('');
  const [inValue, setInValue] = useState(`${day}T09:00`);
  const [outValue, setOutValue] = useState('');
  const [localErr, setLocalErr] = useState('');

  // The journal moved to another day: follow it, so « Ajouter » lands where the operator is looking.
  useEffect(() => {
    setInValue(`${day}T09:00`);
    setOutValue('');
  }, [day]);

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const clockIn = fromDatetimeLocal(inValue);
    const clockOut = fromDatetimeLocal(outValue);
    if (!personId) return setLocalErr('Choisissez une personne.');
    if (!clockIn) return setLocalErr('Heure d’arrivée invalide.');
    if (clockOut !== null && clockOut <= clockIn) return setLocalErr('Le départ doit suivre l’arrivée.');
    setLocalErr('');
    if (await onAdd(personId, clockIn, clockOut)) setOutValue('');
  };

  if (people.length === 0) return null;
  return (
    <form className="hs-form" onSubmit={(ev) => void submit(ev)}>
      <strong>Ajouter un pointage</strong>
      <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
        <option value="">— personne —</option>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {trustedPersonLabel(p)}
          </option>
        ))}
      </select>
      <input type="datetime-local" value={inValue} onChange={(e) => setInValue(e.target.value)} />
      <input type="datetime-local" value={outValue} onChange={(e) => setOutValue(e.target.value)} />
      <button type="submit" className="auth-btn" disabled={busy}>
        Ajouter
      </button>
      {localErr && <span className="auth-err">{localErr}</span>}
    </form>
  );
}

function AddPerson({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (first: string, last: string, nickname: string | null, pin: string) => Promise<boolean>;
}) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [nick, setNick] = useState('');
  const [pin, setPin] = useState('');
  const [localErr, setLocalErr] = useState('');

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!first.trim() || !last.trim()) return setLocalErr('Prénom et nom requis.');
    if (pin !== '' && !/^\d{4,8}$/.test(pin)) return setLocalErr('Le PIN comporte 4 à 8 chiffres.');
    setLocalErr('');
    if (await onAdd(first, last, nick.trim() || null, pin)) {
      setFirst('');
      setLast('');
      setNick('');
      setPin('');
    }
  };

  return (
    <form className="hs-form" onSubmit={(ev) => void submit(ev)}>
      <strong>Ajouter une personne Hors Staff</strong>
      <input placeholder="Prénom" value={first} onChange={(e) => setFirst(e.target.value)} />
      <input placeholder="Nom" value={last} onChange={(e) => setLast(e.target.value)} />
      <input placeholder="Surnom (facultatif)" value={nick} onChange={(e) => setNick(e.target.value)} />
      <input
        placeholder="PIN (4–8 chiffres)"
        inputMode="numeric"
        value={pin}
        onChange={(e) => setPin(e.target.value)}
      />
      <button type="submit" className="auth-btn" disabled={busy}>
        Ajouter
      </button>
      {localErr && <span className="auth-err">{localErr}</span>}
    </form>
  );
}
